import React, { useEffect, useReducer, useState, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdManageAccounts } from "react-icons/md";
import { MdOutlineSubscriptions } from "react-icons/md";
import { MdSubscriptions } from "react-icons/md";
import { SignedIn, SignedOut, useAuth, UserButton, useUser } from '@clerk/clerk-react';
import Cookie from '../../../utils/Cookie';
import { getSocket, SocketConnect, SocketDisconnect } from '../../../socket';
import toast from 'react-hot-toast';
import Reducer from '../../../services/Reducer';
import { CHAT_URL, GET_PUBLIC_PROFILE_URL, USER_LIST } from '../../../utils/apiUrl';
import { CallWithAuth, CallWithOutAuth } from '../../../actions/apiAction';
import { Avatar } from '@mui/material';
import { utcToLocal, formatChatListDate, isOlderThanTwoWeeks } from '../../../utils/helpers';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from "framer-motion";
import FeedbackForm from '../../../components/Feedback/FeedbackFrom';
import useUserContext from '../../../hooks/useUserContext';
import useAuthContext from '../../../hooks/useAuth';
import { selectNotificationTotal, selectUnreadCount } from '../../../redux/slice/notificationSlice';
import { registerPushToken } from '../../../utils/pushSetup';
import BrandLogo from '../../../components/BrandLogo';

const COMMUNITY_GUIDELINES_URL = "https://tlknow.ai/community-standards";

const DEFAULT_MODERATION_WARNING = {
    headline: "Heads up — this breaks TLK's rules",
};

const DEFAULT_SOFT_BAN = {
    headline: "Your account is under review",
};

const MODERATION_BANNER_STATUSES = new Set(["warning", "soft_banned"]);

const initState = {
    userList: '',
    loading: true,
    creatorsList: '',
    creatorsLoading: true,
    creatingChat: false,
};
const CreatorList = () => {
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const username = query.get("username");
    const navigator = useNavigate();
    const [sidePanel, setSidePanel] = useState(false);
    const { user } = useUser();
    const { user: authUser } = useAuthContext();
    const { isInitialized } = useSelector(state => state.socketSlice);
    const { signOut, getToken } = useAuth();

    const [state, dispatch] = useReducer(Reducer, initState);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [moderationWarning, setModerationWarning] = useState(null);
    const [showNotifBanner, setShowNotifBanner] = useState(false);
    const [enablingNotif, setEnablingNotif] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [creatorFirstName, setCreatorFirstName] = useState('');
    const [welcomeStep, setWelcomeStep] = useState(0);

    const { Logout } = useUserContext();
    const notycount = useSelector((state) => state.notificationSlice.count || 0);

    const handleEnableNotifications = async () => {
        if (enablingNotif) return;
        setEnablingNotif(true);
        try {
            const clerkToken = await getToken({ template: "TLK" });
            await registerPushToken(clerkToken);
            setShowNotifBanner(false);
        } catch (err) {
            console.error("Failed to register push token:", err);
            toast.error("Couldn't enable notifications. Please try again.");
        } finally {
            setEnablingNotif(false);
        }
    };

    useEffect(() => {
        try {
            if (typeof window === 'undefined') return;
            if (!('Notification' in window)) return;

            console.log('Notification permission', Notification.permission);

            if (Notification.permission === 'default') {
                setShowNotifBanner(true);
            }
        } catch (err) {
            // iOS Safari throws when accessing Notification.permission
            // outside of installed PWA context — safe to ignore
            console.warn('Notification API not available:', err);
        }
    }, []);
    const showModerationWarningBanner = (payload) => {
        const status = payload?.status === "soft_banned" ? "soft_banned" : "warning";
        const defaults = status === "soft_banned" ? DEFAULT_SOFT_BAN : DEFAULT_MODERATION_WARNING;
        setModerationWarning({
            status,
            headline: payload?.headline || defaults.headline,
        });
    };


    const getUserList = async () => {
        dispatch({ type: "SETDATA", payload: { name: "loading", value: true } });

        let response = await CallWithAuth("GET", CHAT_URL);
        if (response.data) {
            dispatch({ type: "SETDATA", payload: { name: "loading", value: false } });
            dispatch({ type: "SETDATA", payload: { name: "userList", value: response.data } });
        } else {
            dispatch({ type: "SETDATA", payload: { name: "loading", value: false } });
            dispatch({ type: "SETDATA", payload: { name: "userList", value: [] } });

        }
        dispatch({ type: "SETDATA", payload: { name: "loading", value: false } });
    };

    const getCreatorsList = async () => {
        dispatch({ type: "SETDATA", payload: { name: "creatorsLoading", value: true } });

        let response = await CallWithAuth("GET", USER_LIST);
        if (response.data) {
            dispatch({ type: "SETDATA", payload: { name: "creatorsLoading", value: false } });
            dispatch({ type: "SETDATA", payload: { name: "creatorsList", value: response?.data } });
        } else {
            dispatch({ type: "SETDATA", payload: { name: "creatorsLoading", value: false } });
            dispatch({ type: "SETDATA", payload: { name: "creatorsList", value: [] } });
        }
        dispatch({ type: "SETDATA", payload: { name: "creatorsLoading", value: false } });
    };

    const handleChatClick = (item) => {
        Cookie.setCookie('activeChatId', item?._id, 1);
        navigator('/user/chat?id=' + item?.creator_id, { state: { user: item } })
    };

    const handleCreatorClick = async (creator) => {
        if (state.creatingChat) return; // Prevent multiple clicks

        try {
            dispatch({ type: "SETDATA", payload: { name: "creatingChat", value: true } });

            // Create chat with the creator
            const response = await CallWithAuth("POST", CHAT_URL, {
                creator_id: creator.user_id
            });

            if (response.data) {
                // Set the active chat ID
                Cookie.setCookie('activeChatId', response.data._id, 1);

                // Navigate to chat with the response data
                navigator('/user/chat?id=' + creator?.user_id, {
                    state: { user: response.data }
                });
            } else {
                toast.error("Failed to create chat");
                dispatch({ type: "SETDATA", payload: { name: "creatingChat", value: false } });
            }
        } catch (error) {
            console.error("Error creating chat:", error);
            toast.error(error?.response?.data?.detail || "Failed to create chat. Please try again.");
            dispatch({ type: "SETDATA", payload: { name: "creatingChat", value: false } });
        }
    };

    // Filter out creators that are already in user's chats
    const filteredCreators = useMemo(() => {
        // Early return guarantees state.creatorsList and state.userList exist below
        if (!state.creatorsList || !state.userList) return state.creatorsList || [];
        const existingChatCreatorIds = new Set(
            (Array.isArray(state.userList) ? state.userList : []).map(chat => chat.creator_id)
        );
        return state.creatorsList.filter(
            creator => !existingChatCreatorIds.has(creator.user_id)
        );
    }, [state.creatorsList, state.userList]);

    const visibleChats = useMemo(() => {
        const chats = Array.isArray(state.userList) ? state.userList : [];
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return chats;

        return chats.filter((chat) =>
            `${chat?.first_name || ''} ${chat?.last_name || ''}`
                .toLowerCase()
                .includes(normalizedSearch)
        );
    }, [searchTerm, state.userList]);

    const checkUserName = async () => {
        let response = await CallWithAuth("GET", CHAT_URL + `?creator_username=${username}`);
        if (response?.status === 200 && response.data?.creator_id) {
            Cookie.setCookie('activeChatId', response?.data._id, 1);
            navigator('/user/chat?id=' + response.data?.creator_id, {
                state: {
                    user: response.data
                }
            })
        } else {
            toast.error('Creator Not found')
            navigator('/user/creator-list')
        }
    }
    useEffect(() => {
        getUserList();
        getCreatorsList();
    }, [notycount]);

    useEffect(() => {
        if (username) {
            checkUserName()
        }
    }, [username])

    useEffect(() => {
        if (!username) return undefined;
        let cancelled = false;
        CallWithOutAuth('GET', GET_PUBLIC_PROFILE_URL(username)).then((response) => {
            const firstName = response?.data?.first_name;
            if (!cancelled && firstName) setCreatorFirstName(firstName);
        });
        const interval = setInterval(() => {
            setWelcomeStep((step) => (step + 1) % 3);
        }, 2200);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [username]);

    const creatorLabel = creatorFirstName || (username
        ? username.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
        : 'your creator');
    const welcomeMessages = [
        "We're setting things up for you",
        'Warming up your account…',
        `Introducing you to ${creatorLabel}`,
    ];

    useEffect(() => {
        if (MODERATION_BANNER_STATUSES.has(authUser?.status)) {
            showModerationWarningBanner(authUser);
        } else {
            setModerationWarning(null);
        }
    }, [authUser?.status]);

    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        const handleModerationWarning = (data) => {
            showModerationWarningBanner(data);
        };

        socket.on("moderation_warning", handleModerationWarning);
        return () => {
            socket.off("moderation_warning", handleModerationWarning);
        };
    }, [isInitialized]);

    const showFeedbackButton = useMemo(
        () => isOlderThanTwoWeeks(authUser?.created_at),
        [authUser?.created_at]
    );

    useEffect(() => {
        if (!authUser) return;

        console.info('[CreatorList] PWA status effect ran');

        const installed =
            window.matchMedia?.("(display-mode: standalone)")?.matches ||
            window.navigator.standalone === true;

        console.info('[CreatorList] installed PWA:', installed);

        if (authUser.pwa_install !== installed) {
            CallWithAuth('PATCH', '/users/me', { pwa_install: installed })
                .then((response) => {
                    if (!response || response.status === 404) {
                        console.error('[CreatorList] PWA status endpoint returned 404');
                    }
                })
                .catch((err) => {
                    console.error('Failed to sync PWA install status:', err);
                });
        }
    }, [authUser]);
    return (
        <>
            <Helmet>
                <title>Home | TLK</title>
            </Helmet>
            <SignedIn>
                <div className="creator-list-page main-body dark-theme-1">
                    {isInitialized === 'connecting' && (
                        <div className="w-full">

                            <div className=" w-full h-8 bg-gray-700/80 backdrop-blur-sm overflow-hidden flex items-center justify-center">

                                {/* Animated loader */}
                                <motion.div
                                    className="absolute top-0 h-full w-1/3"
                                    style={{
                                        background: 'linear-gradient(100deg, rgb(32, 216, 255) 0%, rgb(138, 92, 255) 50%, rgb(255, 78, 184) 100%)',
                                        boxShadow: '0 0 12px rgb(138, 92, 255)'
                                    }}
                                    initial={{ x: "-100%" }}
                                    animate={{ x: "300%" }}
                                    transition={{
                                        repeat: Infinity,
                                        duration: 1.2,
                                        ease: "linear"
                                    }}
                                />

                                {/* Centered text */}
                                <span className="relative z-10 text-xs text-white font-medium">
                                    Connecting, please wait...
                                </span>

                            </div>

                        </div>
                    )}
                    {username && (
                        <div className="magic-link-welcome" role="status" aria-live="polite">
                            <div className="welcome-card">
                                <div className="spin-ring" aria-hidden="true">
                                    <div className="ring" />
                                </div>
                                <AnimatePresence mode="wait">
                                    <motion.p
                                        key={welcomeStep}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.35 }}
                                    >
                                        {welcomeMessages[welcomeStep]}
                                    </motion.p>
                                </AnimatePresence>
                            </div>
                        </div>
                    )}

                    <div className="creator-list-header main-header-s1">
                        {/* <h1 className="app-title">TLK</h1> */}
                        <BrandLogo alt="TLK" transparent />
                        <div className="right-div">
                            {/* <div className="beta-branding"><img src="/images/beta-branding.svg" alt="" /></div> */}
                            <button aria-label="Open menu" className={sidePanel ? 'menu-toggle active' : 'menu-toggle'} onClick={() => setSidePanel(!sidePanel)}><div></div></button>
                        </div>
                    </div>
                    {showNotifBanner && (
                        <div
                            className=" flex items-center justify-between gap-2 mb-2 px-3 rounded-2xl mx-auto"
                            style={{
                                background: 'var(--neon-gradient) !important',
                                height: '64px',
                                width: 'calc(100% - 24px)',
                                maxWidth: '480px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <div className="flex flex-col min-w-0">
                                <span className="text-white font-semibold text-sm truncate">Enable notifications</span>
                                <span className="text-white/80 text-xs truncate">Get notified when creators message you</span>
                            </div>
                            <button
                                onClick={handleEnableNotifications}
                                disabled={enablingNotif}
                                className="px-3 py-1.5 bg-white text-[#AF0056] text-xs font-semibold rounded-full shrink-0 flex items-center justify-center gap-1.5 min-w-[64px] disabled:opacity-80"
                            >
                                {enablingNotif ? (
                                    <span
                                        className="inline-block w-3.5 h-3.5 rounded-full border-2 border-[#AF0056]/30 border-t-[#AF0056] animate-spin"
                                        aria-label="Enabling notifications"
                                    />
                                ) : (
                                    'Enable'
                                )}
                            </button>
                        </div>
                    )}
                    {moderationWarning && (
                        <div className="px-1 mb-3">
                            <div
                                className="w-full rounded-xl px-3 py-2.5 border"
                                style={{
                                    background: "rgba(120, 53, 15, 0.55)",
                                    borderColor: "rgba(251, 191, 36, 0.45)",
                                }}
                            >
                                <div className="flex items-start gap-2.5">
                                    <div
                                        className="rounded-full flex-shrink-0 flex items-center justify-center"
                                        style={{
                                            width: 28,
                                            height: 28,
                                            background: "#1c1917",
                                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(251, 191, 36, 0.5)",
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" aria-hidden="true">
                                            <path
                                                fill="#facc15"
                                                d="M12 2.5c-.5 0-.95.27-1.2.7L1.9 18.3c-.5.9.15 2 1.2 2h17.8c1.05 0 1.7-1.1 1.2-2L13.2 3.2c-.25-.43-.7-.7-1.2-.7z"
                                            />
                                            <path fill="#1c1917" d="M11.25 9.25h1.5v5.5h-1.5z" />
                                            <circle cx="12" cy="17.25" r="1" fill="#1c1917" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold mb-0.5 leading-snug" style={{ color: "#fde68a" }}>
                                            {moderationWarning?.headline}
                                        </p>
                                        <p className="text-[11px] leading-snug" style={{ color: "rgba(254, 243, 199, 0.88)" }}>
                                            Some of your recent messages go against our{" "}
                                            <Link
                                                to={'/community-guidelines'}
                                                // target="_blank"
                                                // rel="noopener noreferrer"
                                                className="underline font-medium"
                                                style={{ color: "#fef3c7" }}
                                            >
                                                Community Guidelines
                                            </Link>
                                            {moderationWarning?.status === "soft_banned"
                                                ? ". Your account is currently under review. You can still use TLK, but further violations may lead to account suspension."
                                                : ". Please keep things respectful and within the rules. Continued violation of these guidelines may lead to account suspension."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="creator-list-content body-content">
                        {(state.loading || state.creatorsLoading) ? (
                            <div className="flex justify-center items-center py-20 flex-auto">
                                <div className="loaderBox" style={{ position: 'unset', margin: 0, padding: 0, height: 'unset', width: 'unset' }}>
                                    <div className="relative flex items-center justify-center">

                                        {/* Outer glow ring */}
                                        <div
                                            className="w-10 h-10 rounded-full animate-spin"
                                            style={{
                                                background: `conic-gradient(from 0deg, rgb(32, 216, 255), rgb(138, 92, 255), rgb(255, 78, 184), transparent)`,
                                                WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
                                                mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="creator-chat-shell dashboard-section">
                                <div className="creator-list-title">
                                    <span aria-hidden="true"></span>
                                    <h1>My Chats</h1>
                                </div>
                                <label className="creator-chat-search">
                                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="7" />
                                        <path d="m20 20-3.5-3.5" />
                                    </svg>
                                    <input
                                        type="search"
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                        placeholder="Search creators..."
                                        aria-label="Search chats"
                                    />
                                </label>
                                {state.userList?.length > 0 ? (
                                    visibleChats.length > 0 ? <div className="creator-chat-list wp-user-lists">
                                        {visibleChats.map((item, index) => {
                                            return (
                                                <button className="creator-chat-item wp-user-list-item"
                                                    onClick={() => { handleChatClick(item); }}
                                                    key={item?._id || index}
                                                >
                                                    <div className="creator-avatar-ring">
                                                        <div className="user-container">
                                                            <Avatar alt={`${item?.first_name || ''} ${item?.last_name || ''}`} src={item?.profile_picture_url}>{item?.first_name?.charAt(0)}</Avatar>
                                                        </div>
                                                    </div>
                                                    <div className="text-container">
                                                        <div className="title-with-date">
                                                            <h4 style={item?.unread_count > 0 ? { fontWeight: 700 } : {}}>
                                                                {item?.first_name} {item?.last_name}
                                                            </h4>
                                                            <div className="date">{item?.last_message_at ? formatChatListDate(item?.last_message_at) : ''}</div>
                                                        </div>
                                                        <div className="des-with-counter">
                                                            <p style={item?.unread_count > 0 ? { fontWeight: 600, color: 'var(--foreground)', paddingTop: '8px' } : {}}>
                                                                {item?.last_message || "No messages yet"}
                                                            </p>
                                                            {item?.unread_count > 0 && (
                                                                <div className="counter">
                                                                    {item.unread_count > 99 ? '99+' : item.unread_count}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div> : <div className="creator-chat-empty">No chats match your search.</div>
                                ) : <div className="creator-chat-empty">
                                    <div className="creator-chat-empty-icon">⌁</div>
                                    <h3>No active conversations yet</h3>
                                    <p>Visit your favourite creator's magic link and start TLKing now!</p>
                                </div>}

                                {/* <div className="information-card-s1 explore-section">
                                    <div className="body-sub-header-filter-s1 section-header">
                                        <h3>Explore</h3>
                                    </div>
                                    <div className={`explore-creators-grid ${state.creatingChat ? 'disabled' : ''}`}>
                                        {
                                            filteredCreators?.length > 0 && (
                                                filteredCreators.map((creator, index) => {
                                                    return (
                                                        <div
                                                            className="explore-creator-card"
                                                            onClick={() => { handleCreatorClick(creator); }}
                                                            key={index}
                                                        >
                                                            <div className="creator-avatar">
                                                                <Avatar
                                                                    alt={creator?.first_name}
                                                                    src={creator?.profile_picture_url}
                                                                    sx={{ width: 120, height: 120, fontSize: 48 }}
                                                                >
                                                                    {creator?.first_name?.charAt(0)}
                                                                </Avatar>
                                                            </div>
                                                            <div className="creator-info">
                                                                <h4>{creator?.first_name} {creator?.last_name}</h4>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            )
                                        }
                                        {filteredCreators?.length === 0 && (
                                            <div className="no-creators-message">
                                                <p>No new creators to explore</p>
                                            </div>)}
                                        {state.creatingChat && (
                                            <div className="creating-chat-overlay">
                                                <div className="loading-spinner">Creating chat...</div>
                                            </div>
                                        )}
                                    </div>
                                </div> */}
                            </div>
                        )}
                    </div>
                </div>
                {/* Got feedback? floating button */}
                {showFeedbackButton && <div className="fixed bottom-6 left-0 right-0 flex justify-center z-30 pointer-events-none">
                    <button
                        className="pointer-events-auto rounded-full text-white font-medium text-sm shadow-lg shadow-[#AF0056]/30 transition-transform active:scale-95"
                        style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '10px' }}
                        onClick={() => setShowFeedbackForm(true)}
                    >
                        Got feedback?
                    </button>
                </div>}
                {/* Feedback modal */}
                {showFeedbackForm && (
                    <>
                        <motion.div
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowFeedbackForm(false)}
                        />
                        <motion.div
                            className="fixed inset-0 z-50 flex items-center justify-center px-4"
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 40 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        >
                            <div className="w-full scrollbar-hide max-w-[460px] rounded-[40px] shadow-2xl p-8 md:p-10 relative max-h-[92vh] overflow-y-auto border !border-[#ffffff1a] bg-[#0f0e14]/95 backdrop-blur-[40px]"
                                onClick={e => e.stopPropagation()}
                            >
                                <FeedbackForm onClose={() => setShowFeedbackForm(false)} />
                            </div>
                        </motion.div>
                    </>
                )}
                {/* start: side menu */}
                {sidePanel &&
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                        onClick={() => setSidePanel(false)}
                    />
                }
                <div
                    className={`fixed left-0 top-0 bottom-0  w-80 max-w-[85vw] border-r border-[#ffffff0d]  bg-[#0003] shadow-2xl z-50 overflow-y-auto
                    transition ${sidePanel ? 'translate-x-none' : '-translate-x-full'}`}
                >
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-bold heading-text">Menu</h2>
                            <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setSidePanel(false)}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-x w-6 h-6"
                                >
                                    <path d="M18 6 6 18" />
                                    <path d="m6 6 12 12" />
                                </svg>
                            </button>
                        </div>
                        <nav className="space-y-2">
                            <div className="user-information-card">
                                <div className="top-info">
                                    <figure>
                                        <div className="image-container">
                                            <UserButton />
                                        </div>
                                    </figure>
                                    <h2>Welcome, {user?.firstName}</h2>
                                    <h4>Email : {user?.emailAddresses[0]?.emailAddress}</h4>
                                </div>
                            </div>
                            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f4e1e8] transition-colors text-left neon-gradient-bgn-hover"
                                onClick={() => navigator('/user/settings')}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#8a5cff"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-settings w-5 h-5 text-primary"
                                >
                                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                                    <circle cx={12} cy={12} r={3} />
                                </svg>
                                <span className="text-white font-medium">Settings</span>
                            </button>
                            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f4e1e8] transition-colors text-left neon-gradient-bgn-hover"
                                onClick={() => navigator('/user/terms')}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#8a5cff"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-file-text w-5 h-5 text-primary"
                                >
                                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                                    <path d="M10 9H8" />
                                    <path d="M16 13H8" />
                                    <path d="M16 17H8" />
                                </svg>
                                <span className="text-white font-medium">Terms of Service</span>
                            </button>
                            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f4e1e8] transition-colors text-left neon-gradient-bgn-hover"
                                onClick={() => navigator('/user/privacy')}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#8a5cff"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-lock w-5 h-5 text-primary"
                                >
                                    <rect width={18} height={11} x={3} y={11} rx={2} ry={2} />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                                <span className="text-white font-medium">Privacy Policy</span>
                            </button>
                            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f4e1e8] transition-colors text-left neon-gradient-bgn-hover"
                                onClick={() => navigator('/community-guidelines')}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width={24}
                                    height={24}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#8a5cff"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-clipboard-check w-5 h-5 text-primary"
                                >
                                    <rect width={8} height={4} x={8} y={2} rx={1} ry={1} />
                                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                                    <path d="m9 14 2 2 4-4" />
                                </svg>
                                <span className="text-white font-medium">Community Guidelines</span>
                            </button>
                            <div className="pt-3 border-t border-border border-[#ffffff1a] mt-3">
                                <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#e236361a] transition-colors text-left neon-gradient-bgn-hover"
                                    onClick={() => {
                                        SocketDisconnect();
                                        Logout();
                                        Cookie.deleteCookie('userType');
                                        signOut({ redirectUrl: '/' });

                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#db2424"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="lucide lucide-log-out w-5 h-5 text-destructive"
                                    >
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                        <polyline points="16 17 21 12 16 7" />
                                        <line x1={21} x2={9} y1={12} y2={12} />
                                    </svg>
                                    <span className="text-destructive font-medium">Log Out</span>
                                </button>
                            </div>
                        </nav>
                    </div>
                </div>
                {/* end: side menu */}
            </SignedIn>
        </>
    );
}

export default CreatorList;
